/**
 * Deklaratives Dokument-Modell für den PDF-Report-Export.
 *
 * Jede Seite liefert ein {@link ReportDocument} — eine reine Datenbeschreibung
 * ihres Inhalts (Abschnitte / Felder / Tabellen), KEINE UI. Der gemeinsame
 * Renderer ({@link ./renderReport}) erzeugt daraus ein gebrandetes,
 * versandfertiges PDF. So gilt: Engine einmal bauen, jede Seite deklariert nur
 * ihren Inhalt.
 */

/** Firmenlogo für die Kopfzeile (PNG/JPEG als Data-URL, Pixelmaße für das Seitenverhältnis). */
export interface ReportLogo {
  dataUrl: string;
  width: number;
  height: number;
}

export interface ReportMeta {
  /** Projektname — erscheint in der Kopfzeile jeder Seite. */
  projectName: string;
  /** Titel der exportierten Seite, z.B. "Projektauftrag". */
  pageTitle: string;
  /** Optionaler Untertitel unter dem Seitentitel. */
  subtitle?: string;
  /**
   * Erzeugungszeitpunkt. Default (im Renderer): jetzt. Für deterministische
   * Tests explizit setzbar.
   */
  generatedAt?: Date;
  /** Optionales Firmenlogo rechts in der Kopfzeile (PHQ-92). */
  logo?: ReportLogo;
}

export interface ReportDocument {
  sections: ReportSection[];
}

export interface ReportSection {
  /** Abschnitts-Überschrift (Petrol). Optional für titellose Blöcke. */
  title?: string;
  blocks: ReportBlock[];
}

export type ReportBlock =
  | { kind: 'keyValues'; items: ReportKeyValue[] }
  | { kind: 'text'; label?: string; value: string }
  | { kind: 'table'; columns: ReportColumn[]; rows: string[][] }
  | { kind: 'note'; value: string };

export interface ReportKeyValue {
  label: string;
  value: string;
}

export interface ReportColumn {
  header: string;
  /** Relatives Gewicht für die Spaltenbreite (Default 1). */
  weight?: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Builder-Helfer — machen die seitenspezifische Deklaration knapp und robust:
// leere Werte / leere Abschnitte verschwinden automatisch aus dem Report.
// ─────────────────────────────────────────────────────────────────────────────

/** Key-Value-Eintrag — liefert null bei leerem Wert (wird herausgefiltert). */
export function kv(label: string, value: string | null | undefined): ReportKeyValue | null {
  const v = (value ?? '').trim();
  return v ? { label, value: v } : null;
}

/** Key-Value-Block aus einer Liste; null wenn keine befüllten Einträge. */
export function keyValuesBlock(items: (ReportKeyValue | null)[]): ReportBlock | null {
  const filtered = items.filter((i): i is ReportKeyValue => i != null);
  return filtered.length ? { kind: 'keyValues', items: filtered } : null;
}

/** Fließtext-Block mit Label; null wenn leer. */
export function textBlock(label: string, value: string | null | undefined): ReportBlock | null {
  const v = (value ?? '').trim();
  return v ? { kind: 'text', label, value: v } : null;
}

/** Tabellen-Block; null wenn keine Zeilen. */
export function tableBlock(columns: ReportColumn[], rows: string[][]): ReportBlock | null {
  return rows.length ? { kind: 'table', columns, rows } : null;
}

/** Abschnitt aus Blöcken; null wenn alle Blöcke leer/weggefallen sind. */
export function buildSection(
  title: string,
  blocks: (ReportBlock | null)[],
): ReportSection | null {
  const b = blocks.filter((x): x is ReportBlock => x != null);
  return b.length ? { title, blocks: b } : null;
}

/** Dokument aus Abschnitten; filtert weggefallene (null) Abschnitte. */
export function buildDocument(sections: (ReportSection | null)[]): ReportDocument {
  return { sections: sections.filter((s): s is ReportSection => s != null) };
}
